/**
 * B-408 (E3) — okno alertu o braku ważnych uprawnień (CEPiK; B-557: nagłówek nazywa wygasłe / zatrzymane / cofnięte), zamontowane w układzie panelu.
 *
 * Pokazuje się odbiorcom kanału „okno” (ustawienia), dopóki KTOKOLWIEK nie potwierdzi
 * alertu z notatką — potwierdzenie zamyka go dla wszystkich. „Przypomnij później”
 * chowa okno na 15 minut w tej karcie przeglądarki, nie zamyka alertu.
 * Serwer sam filtruje odbiorców — osoba spoza listy dostaje pustą listę.
 *
 * B-418: strzałki „Poprzedni / Następny” przechodzą między czekającymi alertami
 * (pominięty zostaje otwarty), „Wyślij e-mailem” przekazuje alert wybranym osobom.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ShieldAlert, Loader2, CheckCircle2, ExternalLink, Clock, ChevronLeft, ChevronRight, Mail } from 'lucide-react';
import { cn } from '@/shared/lib/utils';
import { api } from '@/shared/lib/api';
import { useAuthStore } from '@store/auth.store';
import { AlertDetails } from './CepikVerificationView';
import { alertHeading, fmtDateTime, errMsg, type CepikAlert } from './cepikShared';
import { AlertMailForm, AlertMailHistory } from './CepikAlertMail';

const SNOOZE_KEY = 'zur-cepik-popup-snooze';
const SNOOZE_MS = 15 * 60_000;

function snoozedUntil(): number {
  try { return Number(sessionStorage.getItem(SNOOZE_KEY)) || 0; } catch { return 0; }
}

export function CepikAlertPopup() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const [snooze, setSnooze] = useState(snoozedUntil);
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(t);
  }, []);

  const q = useQuery({
    queryKey: ['cepik', 'popup'],
    queryFn: () => api.get<CepikAlert[]>('/cepik-verification/alerts/popup'),
    enabled: !!user,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    retry: false,
  });
  const alerts = q.data ?? [];
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [mailOpen, setMailOpen] = useState(false);
  // Pozycja trzymana po ID: po potwierdzeniu lista się skraca i okno pokazuje kolejny, nie wraca na początek.
  const [curId, setCurId] = useState<string | null>(null);
  const [lastIdx, setLastIdx] = useState(0);
  const found = curId ? alerts.findIndex((x) => x.id === curId) : -1;
  const idx = found >= 0 ? found : Math.min(lastIdx, Math.max(alerts.length - 1, 0));
  const a = alerts[idx];
  useEffect(() => { setNote(''); setMailOpen(false); }, [a?.id]);
  useEffect(() => { if (found >= 0) setLastIdx(found); }, [found]);
  const go = (d: number) => {
    if (!alerts.length) return;
    const n = (idx + d + alerts.length) % alerts.length;
    setCurId(alerts[n].id);
    setLastIdx(n);
  };

  if (!a || now < snooze) return null;

  const ack = async () => {
    setBusy(true);
    try {
      await api.post(`/cepik-verification/alerts/${a.id}/ack`, { note });
      toast.success('Alert potwierdzony — zamknięty dla wszystkich odbiorców');
      qc.invalidateQueries({ queryKey: ['cepik'] });
    } catch (e) { toast.error(errMsg(e, 'Nie udało się potwierdzić')); }
    finally { setBusy(false); }
  };
  const later = () => {
    const until = Date.now() + SNOOZE_MS;
    try { sessionStorage.setItem(SNOOZE_KEY, String(until)); } catch { /* prywatne okno */ }
    setSnooze(until);
  };

  return (
    <div className="fixed inset-0 z-[4000] flex items-center justify-center bg-black/55 p-4 backdrop-blur-sm" role="alertdialog" aria-modal="true" aria-labelledby="cepik-alert-title">
      <div className="flex max-h-full w-full max-w-lg flex-col overflow-hidden rounded-2xl border-2 border-red-500 bg-background shadow-2xl">
        <div className="flex shrink-0 items-center gap-3 bg-red-50 px-5 py-3.5 dark:bg-red-500/15">
          <ShieldAlert className="h-6 w-6 shrink-0 text-red-600 dark:text-red-400" />
          <div className="min-w-0">
            <p id="cepik-alert-title" className="font-bold text-red-800 dark:text-red-200">{alertHeading(a)}</p>
            <p className="text-xs text-red-700 dark:text-red-300">Wykryte w CEPiK · {fmtDateTime(a.createdAt)}</p>
          </div>
          {alerts.length > 1 && (
            <div className="ml-auto flex shrink-0 items-center gap-0.5 text-red-800 dark:text-red-200" aria-label="Przechodzenie między alertami">
              <button type="button" onClick={() => go(-1)} className="rounded-md p-1.5 hover:bg-red-100 dark:hover:bg-red-500/20" aria-label="Poprzedni alert" title="Poprzedni alert">
                <ChevronLeft className="h-4 w-4" />
              </button>
              <span className="min-w-[3.25rem] text-center text-xs font-bold tabular-nums">{idx + 1} z {alerts.length}</span>
              <button type="button" onClick={() => go(1)} className="rounded-md p-1.5 hover:bg-red-100 dark:hover:bg-red-500/20" aria-label="Następny alert" title="Następny alert">
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>
        <div className="min-h-0 space-y-3 overflow-y-auto px-5 py-4">
          <p className="text-sm font-semibold">{a.title}</p>
          <AlertDetails a={a} />
          <AlertMailHistory a={a} />
          {mailOpen && <AlertMailForm a={a} onClose={() => setMailOpen(false)} />}
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} autoFocus
            placeholder="Co zrobiono, np. odsunięty od służby, zastępstwo: …"
            className="w-full rounded-lg border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-400/40" />
          <div className="flex flex-wrap items-center justify-end gap-2">
            <button type="button" onClick={later} className="mr-auto inline-flex items-center gap-1.5 text-xs font-semibold text-muted-foreground hover:text-foreground">
              <Clock className="h-3.5 w-3.5" /> Przypomnij za 15 min
            </button>
            <button type="button" onClick={() => setMailOpen((v) => !v)} aria-expanded={mailOpen}
              className={cn('inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 text-sm font-medium hover:bg-muted', mailOpen && 'bg-muted')}>
              <Mail className="h-4 w-4" /> Wyślij e-mailem
            </button>
            <button type="button" onClick={() => { later(); navigate(`/admin/cepik?alert=${a.id}`); }}
              className="inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 text-sm font-medium hover:bg-muted">
              <ExternalLink className="h-4 w-4" /> Otwórz w Kierowcach
            </button>
            <button type="button" onClick={ack} disabled={busy || note.trim().length < 3}
              className="inline-flex items-center gap-1.5 rounded-lg bg-red-600 px-3 py-2 text-sm font-bold text-white hover:bg-red-700 disabled:opacity-40">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Potwierdź i zamknij
            </button>
          </div>
          <p className="text-[11px] text-muted-foreground">
            Potwierdzenie zamyka alert dla wszystkich odbiorców — pozostali zobaczą, kto go obsłużył.
            {alerts.length > 1 ? ` Czeka jeszcze ${alerts.length - 1} ${alerts.length - 1 === 1 ? 'alert' : alerts.length - 1 < 5 ? 'alerty' : 'alertów'}.` : ''}
          </p>
          {alerts.length > 1 && (
            <button type="button" onClick={() => go(1)}
              className="flex w-full items-center justify-center gap-1.5 rounded-lg border border-red-200 bg-red-50/60 py-2 text-sm font-semibold text-red-800 hover:bg-red-100 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-200 dark:hover:bg-red-500/20">
              Następny alert: {alerts[(idx + 1) % alerts.length].details?.driver?.name ?? 'kierowca'} <ChevronRight className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
